import type { UploadErrors } from '@/features/uploadSessionSchedule/atoms';
import {
  FAILURE_ERROR_CODES,
  WARNING_ERROR_CODES,
} from '@/features/uploadSessionSchedule/const';
import type { ScheduledSessionDraft } from '@/features/uploadSessionSchedule/types';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { ScheduleUploadErrorCode } from '@heidi/schema/ml-scribe';
import { isPast } from 'date-fns';

export const calculateUploadErrors = (
  sessions: ScheduledSessionDraft[]
): UploadErrors => {
  const sessionsWithFailures = sessions.filter((session) =>
    session.errors.some((error) => FAILURE_ERROR_CODES.includes(error))
  );

  const sessionsWithWarnings = sessions.filter((session) => {
    const hasWarningError = session.errors.some((error) =>
      WARNING_ERROR_CODES.includes(error)
    );
    const hasPastDate =
      session.datetime && isPast(parseTimeStamp(session.datetime, false));
    return hasWarningError || hasPastDate;
  });

  const failureErrorCodes = new Set<ScheduleUploadErrorCode>(
    sessionsWithFailures.flatMap((session) =>
      session.errors.filter(
        (error): error is ScheduleUploadErrorCode =>
          !!error && FAILURE_ERROR_CODES.includes(error)
      )
    )
  );

  const warningErrorCodes = new Set<ScheduleUploadErrorCode>(
    sessionsWithWarnings.flatMap((session) => {
      const warningErrors = session.errors.filter(
        (error): error is ScheduleUploadErrorCode =>
          !!error && WARNING_ERROR_CODES.includes(error)
      );
      const hasPastDate =
        session.datetime && isPast(parseTimeStamp(session.datetime, false));
      return hasPastDate
        ? [...warningErrors, ScheduleUploadErrorCode.past_date]
        : warningErrors;
    })
  );

  return {
    failures: Array.from(failureErrorCodes),
    warnings: Array.from(warningErrorCodes),
    failureCount: sessionsWithFailures.length,
    warningCount: sessionsWithWarnings.length,
  };
};
